/**
 * Toast notifications for mutation feedback ("Bewertung gespeichert",
 * "Import angewendet", server error details).
 *
 * Deliberately tiny: a provider holding a queue, a `useToast()` hook with
 * `success` / `error` / `info` helpers, and auto-dismiss timers that are cleaned
 * up on unmount. Errors stay longer than successes because they carry the
 * server's German message that the user may need to read.
 */

import { useLingui } from '@lingui/react/macro';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { Icon } from './Icon';

export type ToastKind = 'success' | 'error' | 'info';

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

export interface ToastApi {
  /** Show a toast and return its id (so it can be dismissed early). */
  show: (message: string, kind?: ToastKind, durationMs?: number) => number;
  success: (message: string, durationMs?: number) => number;
  error: (message: string, durationMs?: number) => number;
  info: (message: string, durationMs?: number) => number;
  dismiss: (id: number) => void;
  clear: () => void;
}

const DEFAULT_DURATION: Readonly<Record<ToastKind, number>> = {
  success: 4000,
  info: 5000,
  error: 9000,
};

const ToastContext = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }): ReactNode {
  const { t } = useLingui();
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number): void => {
    const timer = timers.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timers.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const clear = useCallback((): void => {
    for (const timer of timers.current.values()) clearTimeout(timer);
    timers.current.clear();
    setToasts([]);
  }, []);

  const show = useCallback(
    (message: string, kind: ToastKind = 'info', durationMs?: number): number => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, kind, message }]);
      const duration = durationMs ?? DEFAULT_DURATION[kind];
      if (duration > 0) {
        timers.current.set(
          id,
          setTimeout(() => {
            timers.current.delete(id);
            setToasts((current) => current.filter((toast) => toast.id !== id));
          }, duration),
        );
      }
      return id;
    },
    [],
  );

  // Clear pending timers when the provider unmounts.
  useEffect(() => {
    const map = timers.current;
    return () => {
      for (const timer of map.values()) clearTimeout(timer);
      map.clear();
    };
  }, []);

  const api = useMemo<ToastApi>(
    () => ({
      show,
      success: (message, durationMs) => show(message, 'success', durationMs),
      error: (message, durationMs) => show(message, 'error', durationMs),
      info: (message, durationMs) => show(message, 'info', durationMs),
      dismiss,
      clear,
    }),
    [show, dismiss, clear],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className="toast-stack" aria-live="polite" aria-atomic="false">
        {toasts.map((toast) => (
          <div key={toast.id} className={`toast ${toast.kind}`} role={toast.kind === 'error' ? 'alert' : 'status'}>
            <span className="toast-text">{toast.message}</span>
            <button
              type="button"
              className="close-x"
              onClick={() => dismiss(toast.id)}
              aria-label={t`Meldung schließen`}
            >
              <Icon name="close" />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/** Access the toast API. Throws outside `ToastProvider`. */
export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (ctx === null) {
    throw new Error('useToast() muss innerhalb von <ToastProvider> verwendet werden.');
  }
  return ctx;
}
